'use client';

import { useEffect, useRef, useState } from 'react';
import { SALE_LANGS, useSaleLang, type SaleLang } from '@/lib/sale-i18n';

/**
 * Bộ chọn ngôn ngữ /sale — lưu theo thiết bị (localStorage), mặc định theo ngôn ngữ trình duyệt.
 * Dropdown tự vẽ (không dùng <select>): menu native của Chrome/Windows không vẽ emoji cờ.
 */
export default function SaleLangSwitcher() {
  const { lang, setLang } = useSaleLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cur = SALE_LANGS.find(l => l.code === lang) ?? SALE_LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Language"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 border border-[#e4ddd4] rounded-lg bg-white px-2 py-1 text-xs text-[#4a3d30]"
      >
        <span className="text-sm leading-none">{cur.flag}</span>
        <span>{cur.name}</span>
        <span className="text-[10px] text-[#9a8f82]">▾</span>
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 mt-1 z-50 min-w-[10rem] bg-white border border-[#e4ddd4] rounded-lg shadow-lg py-1">
          {SALE_LANGS.map(l => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                onClick={() => { setLang(l.code as SaleLang); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-[#f6f1ea] ${l.code === lang ? 'font-semibold text-[#4a3d30]' : 'text-[#4a3d30]'}`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span>{l.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
